import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@emotion/react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CustomLabel from 'ui-components/CustomLabel';
import GeoMultiSelect from 'ui-components/GeoMultiSelect';
import CustomInputField from 'ui-components/CustomInputField';
import {
  Close as CloseIcon,
  AutoFixHigh as SparkleIcon,
  KeyboardArrowDown as DropdownIcon,
} from '@mui/icons-material';
import ModalComponent from 'ui-components/ModalComponent';
import { useToast } from 'context/ToastContext';
import { useProcessingSnackbar } from 'context/ProcessingSnackbarContext';
import { StartAIShortlistBatch, FetchPipelinesStatuses } from 'api/startup_applications';
import { FetchAllBusinessModel, FetchAllCategories, FetchAllStages, FetchAllSubCategories } from 'api/utils';
import { GetInvestorSetting, FetchDealFlowConfig } from 'api/investors';
import { categoryIdsToNames } from 'utils';
import PerfectScrollbar from 'react-perfect-scrollbar';
import AssessmentFooter from '../../tools/components/AssessmentFooter';
import { Primary } from 'ui-components/Buttons';

const AIShortlistPopup = ({
  open, handleClose, investor_id,
  selectedIds = new Set(),
  selectAll = false,
  selectedCount = 0,
  activeFilters = {},
  investorPipelineId,
  investorStatusId,
  refetchListing, onProcessingChange
}) => {
  const theme = useTheme();
  const { addToast } = useToast();
  const { addBatch, clearCompletedBatches } = useProcessingSnackbar();
  const c = theme.palette.customColors;

  // Matches the Figma spec for this popup's fields: #E5E7EB border, 8px radius.
  const fieldSx = {
    marginBottom: 0,
    '& .MuiOutlinedInput-root': { borderRadius: '8px', minHeight: '44px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB !important' },
    '& > .MuiOutlinedInput-root .MuiOutlinedInput-input': {
      padding: '0.65rem 0.5rem !important',
      height: 'auto', boxSizing: 'border-box'
    },
  };
  // Figma-measured: uppercase, 11px, 600, grey #6B7380, 3% tracking, 100%
  // line-height. lineHeight forced !important — CustomLabel is a Box, not a
  // Typography, but this guarantees the global 1.3 !important can't win. One
  // change here restyles every field label (and GeoMultiSelect's, via its labelSx).
  const labelSx = {
    color: '#6B7380',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    lineHeight: '1 !important',
  };
  const dropdownIcon = <DropdownIcon sx={{ fontSize: 20, color: '#6B7380' }} />;

  // State
  const [shortlistConfig, setShortlistConfig] = useState({
    category: '',
    selectedCategories: '',
    sector: '',
    stage: '',
    business_model: '',
    country: '',
    state: '',
    city: '',
    startup_criteria: '',
    selected_status_id: '',
    rejected_status_id: '',
  });
  const [shortlistErrors, setShortlistErrors] = useState({});

  // API calls
  const { data: investorSetting } = GetInvestorSetting({ investor_id, module: 'deal_flow' });
  const { data: dealFlowConfig } = FetchDealFlowConfig();
  const { mutate: startBatch, isLoading: isStarting } = StartAIShortlistBatch();
  const { mutateAsync: fetchPipelineStatuses } = FetchPipelinesStatuses();
  const { data: categories = [] } = FetchAllCategories();
  const { data: stages = [] } = FetchAllStages();
  const { data: subCategories = [] } = FetchAllSubCategories({ category: shortlistConfig?.selectedCategories?.split(',') || '' });
  const { data: businessModels = [] } = FetchAllBusinessModel();
  const [pipelineStatuses, setPipelineStatuses] = useState([]);

  // Effects
  // Wait for `categories` — selectedCategories (names) drives the sector fetch and has to
  // be derived from the saved category ids when the thesis didn't store it.
  useEffect(() => {
    if (!open) return;
    const thesis = investorSetting?.investment_thesis;
    if (!thesis || !categories?.length) return;
    setShortlistConfig((prev) => ({
      ...prev,
      selected_status_id: thesis.selected_status_id || prev.selected_status_id,
      rejected_status_id: thesis.rejected_status_id || prev.rejected_status_id,
      stage: thesis.stage || prev.stage,
      category: thesis.category || prev.category,
      selectedCategories: thesis.selectedCategories
        || categoryIdsToNames(thesis.category, categories)
        || prev.selectedCategories,
      sector: thesis.sector || prev.sector,
      business_model: thesis.business_model || prev.business_model,
      startup_criteria: thesis.startup_criteria || prev.startup_criteria,
    }));
  }, [open, investorSetting, categories]);

  useEffect(() => {
    if (investor_id && pipelineStatuses.length === 0) {
      fetchPipelineStatuses({ investor_id }, {
        onSuccess: (res) => {
          setPipelineStatuses(
            res?.data?.data?.map((item) => ({ label: item.status, value: item._id, mappedValue: item.value })) || []
          );
        },
      });
    }
  }, [investor_id]);


  // Handlers
  const handleCloseModal = useCallback(() => {
    if (isStarting) {
      addToast('Please wait for the analysis to start.', 'warning');
      return;
    }
    handleClose();
    setShortlistConfig({
      category: '',
      selectedCategories: '',
      sector: '',
      stage: '',
      business_model: '',
      startup_criteria: '',
      selected_status_id: '',
      rejected_status_id: '',
    });
  }, [isStarting, handleClose, addToast]);

  useEffect(() => {
    if (!isStarting) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isStarting]);

  const setConfig = (patch) => {
    setShortlistErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
    setShortlistConfig((prev) => ({ ...prev, ...patch }));
  };

  const multiSelectProps = (field, options, placeholder) => ({
    multiple: true,
    disablePortal: true,
    fullWidth: true,
    autoHighlight: true,
    popupIcon: dropdownIcon,
    options: options.filter((o) => !shortlistConfig[field].split(',').filter(Boolean).includes(o.value)),
    value: shortlistConfig[field].split(',').filter(Boolean).map((v) => ({
      label: options?.find((item) => item?.value === v)?.label,
      value: v,
    })),
    onChange: (_, newVal) => setConfig({ [field]: newVal.map((o) => o.value).join(',') }),
    renderTags: (selected, getTagProps) =>
      selected.map((opt, i) => (
        <Chip key={opt.value} label={opt.label} {...getTagProps({ index: i })} />
      )),
    renderInput: (params) => (
      <CustomInputField
        variant="outlined"
        placeholder={placeholder}
        {...params}
        sx={fieldSx}
        className="filter-autocomplete"
      />
    ),
  });

  const handleStartAnalysis = () => {
    const errors = {};
    if (!shortlistConfig.selected_status_id) errors.selected_status_id = 'Required';
    if (!shortlistConfig.rejected_status_id) errors.rejected_status_id = 'Required';
    if (!shortlistConfig.stage) errors.stage = 'Required';

    if (Object.keys(errors).length > 0) {
      setShortlistErrors(errors);
      return;
    }

    setShortlistErrors({});
    clearCompletedBatches();
    const toArr = (v) => (v ? String(v).split(',').filter(Boolean) : undefined);
    const body = {
      investor_id,
      select_all: selectAll,
      selected_ids: Array.from(selectedIds),
      filters: {
        ...activeFilters,
        ...(toArr(shortlistConfig.country) && { country: toArr(shortlistConfig.country) }),
        ...(toArr(shortlistConfig.state) && { state: toArr(shortlistConfig.state) }),
        ...(toArr(shortlistConfig.city) && { city: toArr(shortlistConfig.city) }),
      },
      ...(investorPipelineId && { investor_pipeline_id: investorPipelineId }),
      ...(investorStatusId && { investor_status_id: investorStatusId }),
      shortlist_config: shortlistConfig,
    };

    startBatch(body, {
      onSuccess: (res) => {
        const newBatchId = res?.data?.batch_id;
        addBatch(newBatchId, {
          batchType: 'dealflow_shortlist',
          uploadStep: 'processing',
          uploadedFileNames: [],
          notifyOnComplete: false,
          count: res?.data?.total_startups
        });
        // onProcessingChange?.(true);
        handleClose();
      },
      onError: (err) => {
        addToast(err?.response?.data?.message || 'Failed to start analysis.', 'error');
      },
    }, {});
  };

  const Header = ({ title, onClose }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: c.g300 }}>
        {title}
      </Typography>
      {onClose && (
        <IconButton onClick={onClose} size="small" sx={{ p: 0.5, color: c.g100, '&:hover': { backgroundColor: c.g50 } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
    </Box>
  );

  const renderSelect = () => (
    <Box sx={{ p: { xs: '1.25rem', sm: '1.75rem 1.75rem 1.5rem' } }}>
      <Header title="AI Shortlisting" onClose={handleCloseModal} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <CustomLabel sx={labelSx}>Shortlisted Status <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
          <Autocomplete
            fullWidth
            disablePortal
            autoHighlight
            popupIcon={dropdownIcon}
            options={pipelineStatuses.filter((s) => {
              const excluded = dealFlowConfig?.deal_flow?.investment_thesis?.excluded_selected_status_ids || [];
              return s.mappedValue !== shortlistConfig.rejected_status_id && !excluded.includes(s.mappedValue);
            })}
            value={pipelineStatuses.find((s) => s.value === shortlistConfig.selected_status_id) || null}
            onChange={(_, newVal) => setConfig({ selected_status_id: newVal?.value || '' })}
            renderInput={(params) => (
              <CustomInputField variant="outlined" placeholder="Select status" {...params} sx={fieldSx} className="filter-autocomplete" />
            )}
          />
          {shortlistErrors.selected_status_id && (
            <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{shortlistErrors.selected_status_id}</Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomLabel sx={labelSx}>Rejected Status <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
          <Autocomplete
            fullWidth
            disablePortal
            autoHighlight
            popupIcon={dropdownIcon}
            options={pipelineStatuses.filter((s) => {
              const excluded = dealFlowConfig?.deal_flow?.investment_thesis?.excluded_rejected_status_ids || [];
              return s.mappedValue !== shortlistConfig.selected_status_id && !excluded.includes(s.mappedValue);
            })}
            value={pipelineStatuses.find((s) => s.value === shortlistConfig.rejected_status_id) || null}
            onChange={(_, newVal) => setConfig({ rejected_status_id: newVal?.value || '' })}
            renderInput={(params) => (
              <CustomInputField variant="outlined" placeholder="Select status" {...params} sx={fieldSx} className="filter-autocomplete" />
            )}
          />
          {shortlistErrors.rejected_status_id && (
            <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{shortlistErrors.rejected_status_id}</Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomLabel sx={labelSx}>Stage <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
          <Autocomplete
            {...multiSelectProps('stage', stages?.map((s) => ({ label: s.name, value: s?._id, _id: s?._id })) || [], 'Stage')}
          />
          {shortlistErrors.stage && (
            <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{shortlistErrors.stage}</Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <CustomLabel sx={labelSx}>Category</CustomLabel>
          <Autocomplete
            {...multiSelectProps('category', categories?.map((cat) => ({ label: cat.name, value: cat?._id, _id: cat?._id })) || [], 'Category')}
            onChange={(_, newVal) => setConfig({
              selectedCategories: newVal.map((o) => o.label).join(','),
              category: newVal?.map(item => item?.value)?.join(','),
              sector: '',
            })}
          />
        </Grid>
        {shortlistConfig.category && (
          <Grid item xs={12} sm={6}>
            <CustomLabel sx={labelSx}>Sector</CustomLabel>
            <Autocomplete
              {...multiSelectProps('sector', subCategories?.map((s) => ({ label: s.sub_category, value: s?._id, _id: s?._id })) || [], 'Sector')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={shortlistConfig.category ? 6 : 12}>
          <CustomLabel sx={labelSx}>Business Model</CustomLabel>
          <Autocomplete
            {...multiSelectProps('business_model', businessModels?.map((s) => ({ label: s.name, value: s?._id, _id: s?._id })) || [], 'Business Model')}
          />
        </Grid>
        {/* <GeoMultiSelect
          labels={{ country: 'Country', state: 'State', city: 'City' }}
          labelSx={labelSx}
          countryIds={shortlistConfig.country ? shortlistConfig.country.split(',').filter(Boolean) : []}
          stateIds={shortlistConfig.state ? shortlistConfig.state.split(',').filter(Boolean) : []}
          cityIds={shortlistConfig.city ? shortlistConfig.city.split(',').filter(Boolean) : []}
          onChange={({ countryIds, stateIds, cityIds }) => {
            setShortlistConfig((prev) => ({
              ...prev,
              country: (countryIds || []).join(','),
              state: (stateIds || []).join(','),
              city: (cityIds || []).join(','),
            }));
          }}
        /> */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <CustomLabel sx={labelSx}>Startup Criteria</CustomLabel>
            <Tooltip title="Describe what makes an ideal startup for your portfolio" placement="top" arrow>
              <Box
                component="span"
                sx={{
                  width: 20, height: 20, borderRadius: '50%',
                  bgcolor: '#F1F1F3',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, mb: '8px', cursor: 'help',
                }}
              >
                <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#71717A', lineHeight: 1 }}>i</Typography>
              </Box>
            </Tooltip>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Example: High-growth SaaS with at least 2 co-founders..."
            value={shortlistConfig?.startup_criteria}
            onChange={(e) => setConfig({ startup_criteria: e.target.value })}
            sx={{
              marginBottom: 0,
              '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: { xs: '0.75rem', sm: '0.875rem' } },
              '& .MuiInputBase-input': { padding: { xs: '0rem', sm: '0rem' } },
              '& .MuiInputBase-input::placeholder': { color: c.g100, opacity: 1 },
              '& fieldset.MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB !important' }
            }}
          />
        </Grid>
      </Grid>

      {/* Figma: centred gradient CTA (not full-width), sparkle + "Shortlist N Startups" */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.75 }}>
        <Primary
          onClick={handleStartAnalysis}
          disabled={isStarting}
          sx={{
            height: '48px',
            px: 4,
            fontSize: '0.95rem',
            fontWeight: 600,
            borderRadius: '10px',
            color: theme.palette.customColors.whiteBG,
            ':disabled': { color: theme.palette.customColors.whiteBG },
          }}
          startIcon={isStarting ? <CircularProgress size={18} sx={{ color: c.whiteBG }} /> : <SparkleIcon />}
        >
          {isStarting ? 'Shortlisting...' : `Shortlist ${selectedCount || 0} Startup${selectedCount !== 1 ? 's' : ''}`}
        </Primary>
      </Box>

      <AssessmentFooter plain />
    </Box>
  );


  return (
    <ModalComponent open={open} onClose={handleCloseModal} style={{ maxWidth: 480, width: '100%' }}>
      {/* Figma: radius 16, drop shadow X0 Y12 blur48 #000 18% */}
      <Paper sx={{ background: c.whiteBG, borderRadius: '16px', overflow: 'hidden', boxShadow: '0px 12px 48px rgba(0, 0, 0, 0.18)' }}>
        <PerfectScrollbar style={{ maxHeight: '90vh' }}>
          {renderSelect()}
        </PerfectScrollbar>
      </Paper>
    </ModalComponent>
  );
};

export default AIShortlistPopup;
